/**
 * WarmupView — LC 생각열기 & 포인트 컴포넌트
 *
 * 현재 레슨 queture 의 생각열기(warmupText) + 포인트(shortPoint) 를 표시하고,
 * 학생이 포인트를 소리내어 읽어 음성으로 녹음(→S3)한다. 녹음 필수 여부는 admin 옵션.
 * 음성 저장은 buildup 과 동일 엔드포인트(slot 1)이며, 지정 복습에서는 learningAttemptId 문맥으로
 * 서버가 회차 격리 기록에 저장한다(최초 녹음 불변).
 * 회차별 정책(required | optional)은 서버가 해석해 warmupData.policy/skipAllowed 로 내려준다.
 * optional 이면 [건너뛰기]로 녹음 없이 다음 단계로 진행할 수 있고, 재진입 시 서버의 skipped 상태를 복원한다.
 * Spec: docs/superpowers/specs/2026-06-09-lc-warmup-point-component-design.md,
 *       docs/superpowers/specs/2026-09-09-lc-review-warmup-policy-design.md
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Lightbulb } from 'lucide-react'
import {
  completeSession,
  getAudioUploadUrl,
  saveBuildupAudioResponse,
  skipSession,
  uploadAudioToS3,
  type ComponentCompleteResult,
  type LcWarmupData,
} from '@/api/lcLearning'
import { stripShortPointPrefix, WarmupTextView } from '@/components/common/WarmupTextView'
import textSizeDownIcon from '@/assets/learner/warmup/text-size-down.svg'
import textSizeUpIcon from '@/assets/learner/warmup/text-size-up.svg'
import WarmupAudioRecorder from './WarmupAudioRecorder'
import { Button } from './ui/Button'
import type { RuntimeMode } from './ComponentRunner'

interface Props {
  componentProgressId: number | null
  enrollmentId: number
  componentType: string
  label: string
  stepNumber?: number
  warmupData: LcWarmupData | null
  runtimeMode?: RuntimeMode
  /** 지정 복습이면 시작 응답의 회차 ID — 스킵·음성 저장 문맥. 첫 학습·도서관은 null. */
  learningAttemptId?: number | null
  onComplete: (result?: ComponentCompleteResult) => void
}

export default function WarmupView({
  componentProgressId,
  enrollmentId,
  componentType,
  label: _label,
  stepNumber = 6,
  warmupData,
  runtimeMode = 'live',
  learningAttemptId = null,
  onComplete,
}: Props) {
  const { t } = useTranslation('learner')
  const isLive = runtimeMode === 'live'
  const readOnly = runtimeMode === 'library-review'

  const audioEnabled = warmupData?.audioEnabled ?? false
  const audioRequired = warmupData?.audioRequired ?? false
  // 서버가 해석한 회차 정책. 구버전 응답(필드 없음)은 required 로 취급 — 스킵 허용이 확산되지 않도록.
  const isOptional = isLive && (warmupData?.policy ?? 'required') === 'optional'
  const skipAllowed = isOptional && (warmupData?.skipAllowed ?? false)
  const alreadySkipped = isLive && warmupData?.componentStatus === 'skipped'
  const contentMissing = isLive && (warmupData?.contentMissing ?? false)

  const [audioSubmitted, setAudioSubmitted] = useState<boolean>(!!warmupData?.audioResponseUrl)
  const [recordingSkipped, setRecordingSkipped] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [completing, setCompleting] = useState(false)
  const [skipping, setSkipping] = useState(false)
  const [largeText, setLargeText] = useState(false)
  const busy = completing || skipping

  // 콘텐츠 없음: 서버가 이미 content_missing 으로 기록했으므로 API 호출 없이 1회 자동 통과.
  const autoAdvancedRef = useRef(false)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  useEffect(() => {
    if (!contentMissing || autoAdvancedRef.current) return
    autoAdvancedRef.current = true
    onCompleteRef.current({ status: 'skipped', skipReason: warmupData?.skipReason ?? 'content_missing' })
  }, [contentMissing, warmupData?.skipReason])

  const handleAudioSubmit = useCallback(
    async (blob: Blob) => {
      if (!isLive || componentProgressId == null) {
        setAudioSubmitted(true)
        return
      }
      const { uploadUrl, fileUrl } = await getAudioUploadUrl(componentProgressId, 1, learningAttemptId)
      await uploadAudioToS3(uploadUrl, blob)
      await saveBuildupAudioResponse(componentProgressId, 1, fileUrl, learningAttemptId)
      setAudioSubmitted(true)
    },
    [isLive, componentProgressId, learningAttemptId],
  )

  const handleComplete = useCallback(async () => {
    if (busy) return
    setError(null)
    // 이미 건너뛴 단계(재진입): 재수행을 요구하지 않고 서버 상태 그대로 다음으로.
    if (alreadySkipped) {
      onComplete({ status: 'skipped', skipReason: warmupData?.skipReason ?? 'learner_choice' })
      return
    }
    setCompleting(true)
    try {
      if (isLive && componentProgressId != null) {
        const result = await completeSession(componentProgressId, { enrollmentId, componentType })
        // 늦은 complete 가 skipped 를 유지한 경우 서버 결과를 그대로 반영한다.
        onComplete(
          result?.componentStatus === 'skipped'
            ? { status: 'skipped', skipReason: result.skipReason ?? 'learner_choice' }
            : { status: 'completed' },
        )
        return
      }
      onComplete({ status: 'completed' })
    } catch {
      setError(t('warmup.errors.complete'))
      setCompleting(false)
    }
  }, [busy, alreadySkipped, isLive, componentProgressId, enrollmentId, componentType, onComplete, t, warmupData?.skipReason])

  const handleSkip = useCallback(async () => {
    if (busy || !skipAllowed) return
    setError(null)
    setSkipping(true)
    try {
      if (componentProgressId != null) {
        const result = await skipSession(componentProgressId, { enrollmentId, learningAttemptId })
        onComplete(
          result.componentStatus === 'completed'
            ? { status: 'completed' }
            : { status: 'skipped', skipReason: result.skipReason ?? 'learner_choice' },
        )
        return
      }
      onComplete({ status: 'skipped', skipReason: 'learner_choice' })
    } catch {
      setError(t('warmup.errors.skip'))
      setSkipping(false)
    }
  }, [busy, skipAllowed, componentProgressId, enrollmentId, learningAttemptId, onComplete, t])

  const canComplete =
    alreadySkipped || !audioEnabled || !audioRequired || audioSubmitted || recordingSkipped
  const showRecorder = audioEnabled && (!readOnly || Boolean(warmupData?.audioResponseUrl))

  return (
    <div
      data-warmup-root
      className="flex min-h-[596px] w-full flex-col items-end justify-between gap-5"
    >
      <div
        data-warmup-card-row
        className="grid w-full max-w-[1136px] auto-rows-fr grid-cols-1 items-stretch gap-6 lg:min-h-[528px] lg:grid-cols-[minmax(0,1fr)_400px]"
      >
        <section
          data-warmup-text-card
          className="flex min-h-[528px] min-w-0 flex-col gap-6 rounded-[20px] bg-gradient-to-r from-white to-[#f9fafb] px-5 pb-6 pt-[26px] shadow-[0_4px_8px_0_rgb(0_0_0/0.05)]"
        >
          <div className="flex flex-col gap-4 px-2 pt-0.5">
            <div className="flex shrink-0 items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-lc-warning-50 text-lc-warning-700">
                  <Lightbulb className="size-5" />
                </span>
                <h2 className="text-[18px] font-bold leading-normal text-[#1c1c1e]">
                  {t('warmup.thinkOpen')}
                </h2>
                {isOptional ? (
                  <span
                    data-warmup-optional-badge
                    className="rounded-full border border-lc-v3-gray-300 bg-white px-2 py-0.5 text-[12px] font-semibold leading-[1.6] text-lc-text-secondary"
                  >
                    {t('warmup.optionalBadge')}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                aria-pressed={largeText}
                onClick={() => setLargeText((value) => !value)}
                className="flex shrink-0 items-center gap-1.5 rounded-full border border-lc-v3-gray-300 bg-lc-v3-gray-50 py-1.5 pl-2.5 pr-3 text-[12px] font-semibold leading-[1.6] text-lc-text-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
              >
                <img
                  src={largeText ? textSizeDownIcon : textSizeUpIcon}
                  alt=""
                  aria-hidden="true"
                  className="size-5"
                />
                {largeText ? t('warmup.textSize.normal') : t('warmup.textSize.large')}
              </button>
            </div>

            <div data-warmup-text-body className="pr-1">
              {warmupData?.warmupText ? (
                <WarmupTextView
                  text={warmupData.warmupText}
                  className={`${largeText ? 'text-[20px]' : 'text-[16px]'} leading-[1.6] text-lc-text-primary`}
                />
              ) : (
                <p className="text-[16px] leading-[1.6] text-lc-text-tertiary">{t('warmup.empty')}</p>
              )}
            </div>
          </div>

          <div
            data-warmup-point
            className="flex w-full shrink-0 flex-col items-start justify-center gap-2 rounded-2xl bg-lc-bg-key-light px-4 pb-4 pt-3.5"
          >
            <span className="rounded-full bg-lc-key-500 px-3 py-1.5 text-[14px] font-semibold leading-none tracking-[0.2px] text-white">
              {t('warmup.point')}
            </span>
            {warmupData?.shortPoint ? (
              <WarmupTextView
                text={stripShortPointPrefix(warmupData.shortPoint)}
                className={`${largeText ? 'text-[20px]' : 'text-[16px]'} font-bold leading-[1.6] text-lc-text-primary`}
              />
            ) : (
              <p className="text-[16px] leading-[1.6] text-lc-text-tertiary">{t('warmup.empty')}</p>
            )}
          </div>
        </section>

        {showRecorder ? (
          <section
            data-warmup-record-card
            className="flex min-h-[528px] w-full flex-col items-center overflow-hidden rounded-[20px] bg-white shadow-[0_4px_8px_0_rgb(0_0_0/0.05)]"
          >
            <header className="flex w-full shrink-0 flex-col items-center gap-2 px-6 pt-8 text-center font-bold">
              <p className="text-[14px] leading-[1.6] tracking-[0.1px] text-lc-text-disabled">
                {t('warmup.step', { number: stepNumber })}
              </p>
              <h2 className="text-[24px] leading-[1.45] tracking-[-0.2px] text-[#1c1c1e]">
                {t('warmup.title')}
              </h2>
            </header>
            <div className="flex min-h-0 w-[calc(100%-48px)] max-w-[316px] flex-1">
              <WarmupAudioRecorder
                onSubmit={handleAudioSubmit}
                existingUrl={warmupData?.audioResponseUrl ?? null}
                unavailableActionLabel={audioRequired ? t('warmup.skipRecording') : undefined}
                onUnavailableAction={
                  audioRequired && !recordingSkipped
                    ? () => setRecordingSkipped(true)
                    : undefined
                }
              />
            </div>
          </section>
        ) : null}
      </div>

      <div
        className={`flex w-full flex-col items-center gap-2 pt-3 ${skipAllowed && !alreadySkipped ? 'max-w-[336px]' : 'max-w-[160px]'}`}
      >
        {error ? (
          <p role="alert" className="flex items-center gap-1 text-[13px] text-lc-text-error">
            <AlertCircle className="size-4" />
            {error}
          </p>
        ) : null}
        {contentMissing ? (
          <p role="status" className="text-center text-[13px] text-lc-text-secondary">
            {t('warmup.contentMissingNotice')}
          </p>
        ) : null}
        {alreadySkipped && !contentMissing ? (
          <p role="status" className="text-center text-[13px] text-lc-text-secondary">
            {t('warmup.skippedNotice')}
          </p>
        ) : null}
        {!readOnly && !alreadySkipped && audioEnabled && audioRequired && !audioSubmitted && !recordingSkipped ? (
          <p className="text-center text-[13px] text-lc-text-tertiary">
            {skipAllowed ? t('warmup.optionalHint') : t('warmup.requiredHint')}
          </p>
        ) : null}
        {!readOnly && recordingSkipped ? (
          <p className="text-center text-[13px] text-lc-text-secondary">
            {t('warmup.recordingSkipped')}
          </p>
        ) : null}
        {/* Button 기본 shrink-0 이라 두 버튼이 나란히 설 때 넘치지 않도록 flex-1·min-w-0 으로 폭을 나눈다. */}
        <div className="flex w-full items-stretch gap-2">
          {skipAllowed && !alreadySkipped ? (
            <Button
              variant="outline"
              size="XL"
              className="min-w-0 flex-1 shrink"
              onClick={handleSkip}
              disabled={busy}
            >
              {skipping ? t('warmup.skipping') : t('warmup.skip')}
            </Button>
          ) : null}
          <Button
            variant="navy"
            size="XL"
            className="min-w-0 flex-1 shrink"
            onClick={handleComplete}
            disabled={busy || (!readOnly && !canComplete)}
          >
            {completing
              ? t('warmup.completing')
              : readOnly
                ? t('warmup.complete')
                : t('warmup.next')}
          </Button>
        </div>
      </div>
    </div>
  )
}
